import React, { FC } from 'react';
import styled, { css } from 'styled-components';
import { ProcessFragment } from '../types/graphqlTypes';
import { ButtonRenderer } from './buttonRenderer/ButtonRenderer';
import { Img } from './Img';
import { Panel } from './Panel';
import { Spacer } from './Spacer';
import { Typography } from './Typography';

const StyledProcess = styled.ol`
  ${({ theme: { colors, space, media } }) => css`
    position: relative;
    margin-top: ${space.lg};

    &:before {
      content: '';
      display: block;
      width: 1px;
      height: 100%;
      position: absolute;
      left: 50%;
      top: 0;
      background-color: ${colors.grey3.default};
    }
  `};
`;

const StyledNumber = styled(Typography)`
  ${({ theme: { colors, space, media, global } }) => css`
    background: ${colors.brand.default};
    color: ${colors.brand.contrast};
    display: grid;
    place-items: center;
    width: ${space.xl};
    height: ${space.xl};
    border-radius: ${global.borderRadius.default};
    position: absolute;
    top: -${space.lg};
    left: ${space.lg};
  `};
`;

const StyledStepInner = styled.div`
  ${({ theme: { space, media } }) => css`
    // Desktop > styles

    @media (min-width: ${media.lg}) {
      display: flex;
      gap: ${space.lg};
      justify-content: space-between;
    }
  `};
`;

const StyledImage = styled.div`
  flex-shrink: 0;
`;

const StyledStepBody = styled(Spacer)`
  ${({ theme: { space, media } }) => css`
    margin: ${space.lg} 0;

    // Desktop > styles

    @media (min-width: ${media.lg}) {
      margin: ${space.lg} 0 0 0;
    }
  `};
`;

const StyledStep = styled(Panel)`
  ${({ theme: { space, media } }) => css`
    position: relative;

    & + & {
      margin-top: ${space.xxl};
    }

    // Desktop > styles

    @media (min-width: ${media.lg}) {
      & + & {
        margin-top: ${space.xxxl};
      }

      &:nth-child(even) {
        ${StyledNumber} {
          right: ${space.lg};
          left: auto;
        }

        ${StyledStepInner} {
          flex-direction: row-reverse;
        }
      }
    }
  `};
`;

// JSX

export const Process: FC<ProcessFragment> = ({ steps }) => {
  if (steps.length < 1) {
    return <></>;
  }

  return (
    <StyledProcess>
      {steps.map(({ title, text, image, button }, index) => (
        <StyledStep key={index} forwardedAs="li" border backgroundColor="white">
          <StyledNumber component="span" variant="titleLarge">
            {index + 1}
          </StyledNumber>
          <StyledStepInner>
            <StyledStepBody spacing="half">
              <Typography component="h3" variant="titleMedium">
                {title}
              </Typography>
              <p>{text}</p>
              {button && <ButtonRenderer button={button} arrowLink />}
            </StyledStepBody>

            {image && (
              <StyledImage>
                <Img src={image} />
              </StyledImage>
            )}
          </StyledStepInner>
        </StyledStep>
      ))}
    </StyledProcess>
  );
};
